import {EmailFlow, UnlayerDesign} from '../util/interfaces';

import {
    createDefaultEmail,
    deleteDefaultEmail,
    getDefaultEmail,
    getDefaultEmails,
    getDefaultEmailsAdmin,
    updateDefaultEmail
} from '../util/requestSender';

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';

import {deepClone} from '../util/utils';

interface Props extends BaseProps {
    template_name?: string;
    email_subject?: string;
    json_template?: UnlayerDesign;
    email_flows?: Array<EmailFlow>;
    enabled?: boolean;
    preview_file_id?: string;
    preview_url?: string | null;
    preview_url_error?: boolean;
}

export interface DefaultEmailFields extends BaseFields {
    template_name: string;
    email_subject: string;
    json_template: UnlayerDesign;
    email_flows: Array<EmailFlow>;
    enabled: boolean;
    preview_file_id: string;
    preview_url: string | null;
    preview_url_error: boolean;
}

const fields: DefaultEmailFields = {
    id: '',
    created_at: '',
    updated_at: '',
    template_name: '',
    email_subject: '',
    json_template: {} as UnlayerDesign,
    email_flows: [],
    enabled: true,
    preview_file_id: '',
    preview_url: '',
    preview_url_error: false
};

const fieldsDefinition: {[key in keyof DefaultEmailFields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    template_name: createFieldDef(ModelFieldType.STRING),
    email_subject: createFieldDef(ModelFieldType.STRING),
    enabled: createFieldDef(ModelFieldType.BOOLEAN),
    preview_file_id: createFieldDef(ModelFieldType.STRING),
    preview_url: createFieldDef(ModelFieldType.STRING),
    email_flows: createFieldDef(ModelFieldType.ARRAY),
    json_template: createFieldDef(ModelFieldType.OBJECT),
    preview_url_error: createFieldDef(ModelFieldType.BOOLEAN)
};

const ignoreProps: Array<keyof DefaultEmailFields> = [];

export default class DefaultEmail extends BaseModel<Props, DefaultEmailFields> {
    static get = (id: string, callback: ModelResponse<DefaultEmail | null>) => {
        return getDefaultEmail({
            id,
            callback: (data: ResponseData) => {
                const defaultEmail = data.success ? new DefaultEmail(data.default_email) : null;
                callback(defaultEmail, data.success, data);
            }
        });
    };

    static getAllAdmin = (config: {
        search?: string | null;
        flows?: Array<EmailFlow>;
        enabled?: boolean | null;
        page: number;
        limit: number;
        callback: ModelResponse<Array<DefaultEmail>>;
    }) => {
        return getDefaultEmailsAdmin({
            data: {
                email_flows: config.flows,
                page: config.page,
                enabled: config.enabled,
                limit: config.limit,
                search: nullify(config.search)
            },
            callback: (data: ResponseData) => {
                const defaultEmails: Array<DefaultEmail> = data.success
                    ? data.default_emails.map((email: any) => new DefaultEmail(email))
                    : [];
                config.callback(defaultEmails, data.success, data);
            }
        });
    };

    static getAll = (config: {flows: Array<EmailFlow>; callback: ModelResponse<Array<DefaultEmail>>}) => {
        return getDefaultEmails({
            data: {email_flows: config.flows},
            callback: (data: ResponseData) => {
                const defaultEmails: Array<DefaultEmail> = data.success
                    ? data.default_emails.map((email: any) => new DefaultEmail(email))
                    : [];
                config.callback(defaultEmails, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get templateName(): string {
        return this.fields.template_name;
    }

    set templateName(template_name: string) {
        this.fields.template_name = template_name;
    }

    get emailSubject(): string {
        return this.fields.email_subject;
    }

    set emailSubject(subject: string) {
        this.fields.email_subject = subject;
    }

    get jsonTemplate(): UnlayerDesign {
        return this.fields.json_template;
    }

    set jsonTemplate(json_template: UnlayerDesign) {
        this.fields.json_template = json_template;
    }

    get emailFlows(): Array<EmailFlow> {
        return this.fields.email_flows;
    }

    set emailFlows(email_flows: Array<EmailFlow>) {
        this.fields.email_flows = email_flows;
    }

    get enabled(): boolean {
        return this.fields.enabled;
    }

    set enabled(enabled: boolean) {
        this.fields.enabled = enabled;
    }

    get previewFileId(): string {
        return this.fields.preview_file_id;
    }

    set previewFileId(preview_file_id: string) {
        this.fields.preview_file_id = preview_file_id;
    }

    get previewURL(): string | null {
        return this.fields.preview_url;
    }

    set previewURL(preview_url: string | null) {
        this.fields.preview_url = preview_url;
    }

    get previewURLError(): boolean {
        return this.fields.preview_url_error;
    }

    set previewURLError(error: boolean) {
        this.fields.preview_url_error = error;
    }

    create = (previewFile: File | null, callback: ModelResponse<DefaultEmail>) => {
        return createDefaultEmail({
            data: {
                email_flows: this.emailFlows,
                enabled: this.enabled,
                json_template: this.jsonTemplate,
                template_name: this.templateName,
                preview_file: previewFile,
                email_subject: this.emailSubject
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.default_email);
                callback(this, data.success, data);
            }
        });
    };

    update = (previewFile: File | null, callback: ModelResponse<DefaultEmail>) => {
        return updateDefaultEmail({
            data: {
                id: this.id,
                email_flows: this.emailFlows,
                enabled: this.enabled,
                json_template: this.jsonTemplate,
                template_name: this.templateName,
                preview_file: previewFile,
                email_subject: this.emailSubject,
                preview_file_id: nullify(this.previewFileId)
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.default_email);
                callback(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<DefaultEmail>) => {
        return deleteDefaultEmail({
            id: this.id,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    clone = () => {
        const fields = deepClone(this.fields);
        return new DefaultEmail(fields);
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}
